import {useCallback, useEffect, useMemo, useState} from 'react';
import {Provider} from 'react-redux';
import {call, delay, fork, join, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {Route, Switch, useLocation} from "react-router-dom";
import styled, {ThemeProvider} from 'styled-components';

import {useColorMode} from "@chakra-ui/react";
import ReactGA from "react-ga4";
import useTheme from './theme';
import {AppContext} from './app_context';
import {useLoggedIn, useStoreProfile} from './store';
import {actionEffects as actions} from "./store/actions";
import {selectProfile, selectUsername} from "./store/selectors";
import {clearApiCredentials, loadApiCredentials} from "./api/client";
import {autoSignIn} from './api/sign_in';
import {loadUserProfile, saveSettings} from './api/user';
import {periodicEventChannel} from "./utils/channels";
import useFeedback from './utils/use_feedback';
import {bypassingReadyChecks, signalReady, useSagaEffect} from './utils/sagas';
import StyledToastContainer from "./components/styled_toast_container";
import AppContainer from "./components/app_container";
import BottomBar from "./components/bottom_bar";
import ErrorBoundary from "./components/error_boundary";
import {useModalStack} from "./modals/registry";

import Listing from './routes/listing';
import Profile from './routes/profile';
import Settings from './routes/settings';
import Post from './routes/post';
import Communities from './routes/communities';
import Messages from './routes/messages';
import Message from './routes/message';
import Login from './routes/login';
import Registration from './routes/registration';
import AltPosts from './routes/alt_posts';
import Submit from './routes/submit';
import Saved from './routes/saved';
import SaveFolder from './routes/save_folder';
import Search from './routes/search';
import Send from "./routes/send";
import Queue from "./routes/queue";
import Logs from "./routes/logs";
import Blocks from "./routes/blocks";
import FabWrapper from "./components/fab_wrapper";
import Feeds from "./routes/feeds";
import Browse from "./routes/browse";
import AboutCommunity from "./routes/community/about";
import WikiCommunity from "./routes/community/wiki";
import Create from "./routes/create";
import CommunitySettings from "./routes/community_config/dashboard";
import BasicCommunitySettings from "./routes/community_config/basic";
import DesignCommunitySettings from "./routes/community_config/design";
import TopicsCommunitySettings from "./routes/community_config/topics";
import ModeratorsCommunitySettings from "./routes/community_config/moderators";
import FilterCommunitySettings from "./routes/community_config/filter";
import StylesheetCommunitySettings from "./routes/community_config/stylesheet";
import RulesCommunitySettings from "./routes/community_config/rules";
import VotingCommunitySettings from "./routes/community_config/voting";
import ContentCommunitySettings from "./routes/community_config/content";
import PrivacyCommunitySettings from "./routes/community_config/privacy";
import Ban from "./routes/ban";
import {loadAdConfigSaga} from "./api/ads";
import NotificationsSettings from "./routes/settings/notifications";
import PrivacySettings from "./routes/settings/privacy";
import PasswordSettings from "./routes/settings/password";
import PurgeSettings from "./routes/settings/purge";
import DeleteSettings from "./routes/settings/delete";
import EmailSettings from "./routes/settings/email";
import AppearanceSettings from "./routes/settings/appearance";
import GeneralSettings from "./routes/settings/general";
import SubscriptionSettings from "./routes/settings/subscriptions";

import useGATag, {useUmamiTag} from "./utils/analytics";
import {ModalProvider} from "./components/test/ModalContext";
import ModalContainer from "./components/test/ModalContainer";
import BlockedCommunities from "./routes/blocked_communities";
import ProfileSettings from "./routes/settings/profile";
import Reset from "./routes/reset";
import Change from "./routes/change";
import useRouteChange from "./utils/useRouteChange";
import useVisitorId from "./utils/ad_utils";
import {handleRouteChange} from "./utils/route_change";
import TrackingPixels from "./components/pixels/tracking_pixels";
import RulesCommunity from "./routes/community/rules";
import RelatedCommunity from "./routes/community/related";
import BottomHeadroom from "./components/bottom_headroom";
import useWindowDimensions from "./utils/dimensions";

const Bottom = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;`;

ReactGA.initialize(useGATag());

function failsafe(saga) {
    return saga;
    /*
    if (import.meta.env.DEV || import.meta.env.VITE_STAGING) {
        return saga;
    }
            // silently ignored
        }
    };
    */
}

function* browserStartUpSaga() {

    const tasks = [];

    try {
        tasks.push(yield fork(loadAdConfigSaga));
    } catch {
        console.log("failed to load ad config");
    }

    // Clean up localStorage from now unused keys.
    yield call([localStorage, 'removeItem'], 'username');

    // Synchronize settings with storage.
    try {
        const settings = JSON.parse(yield call([localStorage, 'getItem'], 'settings') || 'null');
        yield actions.settingsLoaded(settings);
    } catch {
        // Clear bad settings.
        yield call([localStorage, 'removeItem'], 'settings');
    }
    yield takeEvery(['settingsLoaded', 'settingsChanged'], function* () {
        const settings = yield select(selectProfile);
        yield call([localStorage, 'setItem'], 'settings', JSON.stringify(settings));
    });
    let combinedChanges = {};
    yield takeLatest('settingsChanged', failsafe(function* ({payload: {changes}}) {
        const loggedIn = yield select((state) => Boolean(selectUsername(state)));
        if (loggedIn) {
            Object.assign(combinedChanges, changes);
            yield delay(100);
            yield call(saveSettings, combinedChanges);
            combinedChanges = {};
        }
    }));

    // Load communities in the background.
    try {
        const communities = JSON.parse(yield call([localStorage, 'getItem'], 'communities') || 'null');
        if (communities) {
            actions.communitiesLoaded(communities);
        }
    } catch {
        // Clear bad communities.
        yield call([localStorage, 'removeItem'], 'communities');
    }

    // Persist communities to local storage whenever they change.
    // Update to profile/settings cascade to communities (see uses of updateCommunities).
    let lastCommunityMap = null;
    yield takeEvery('*', function* () {
        const communityMap = yield select(({ communityMap }) => communityMap);
        if (lastCommunityMap !== null && lastCommunityMap !== communityMap) {
            const communities = Array.from(communityMap.values());
            yield call([localStorage, 'setItem'], 'communities', JSON.stringify(communities));
        }
        lastCommunityMap = communityMap;
    });

    // Clear localstorage on logout.
    yield takeEvery('logout', function* () {
        yield call([localStorage, 'clear']);
    });

    // Dispatch an action every minute.
    const minute = yield call(periodicEventChannel, 60 * 1000);
    yield takeEvery(minute, failsafe(function* () {
        try {
            yield actions.timeChanged(new Date());
            // Reload user profile to update unread message count.
            yield call(loadUserProfile);
        } catch {
            console.warn(`exception in 1min periodic task`);
        }
    }));

    // Update the service worker every hour.
    const hourly = yield call(periodicEventChannel, 60 * 60 * 1000);
    yield takeEvery(hourly, function* () {
        const registration = yield select(({ registration }) => registration);
        yield call([registration, 'update']);
    });

    // Allow API calls to proceed.
    yield call(signalReady);

    // Attempt to sign in and update profile/subscribed communities in the background.
    yield call(bypassingReadyChecks, function*() {
        let retries = 0;
        while (retries < 2) {
            if (!loadApiCredentials()) {
                yield call(autoSignIn);
            }
            try {
                yield call(loadUserProfile);
                return;
            } catch (e) {
                if (e.toString() === "Error: invalid user") {
                    yield call(clearApiCredentials);
                }
            }
            retries++;
        }
    });

    // Ensure background tasks are done before returning (not strictly necessary).
    yield join(tasks);
}



const MainRouter = () => {

    const {feedbackCh} = useFeedback();
    useSagaEffect(feedbackCh, browserStartUpSaga);

    const location = useLocation();

    useEffect(() => {
        try {
            const urlParams = new URLSearchParams(location.search);
            const referrer = urlParams.get('ref') || urlParams.get('utm_campaign');

            if (referrer) {
                localStorage.setItem('referrer', referrer);
            }

            const utmSource = urlParams.get('utm_source');
            const utmCampaign = urlParams.get('utm_campaign');
            if (utmSource && utmCampaign) {
                localStorage.setItem('utm', `${utmSource}-${utmCampaign}`);
            }

        } catch (error) {
            console.error('Error setting referrer in localStorage:', error);
        }
    }, [location.search]);

    const visitorId = useVisitorId();
    const { referrer: signupReferrer, username } = useStoreProfile();

    const memoizedHandleRouteChange = useCallback(handleRouteChange(visitorId, 'pv', signupReferrer, username), [visitorId, signupReferrer]);

    useRouteChange(memoizedHandleRouteChange);

    return (
        <Switch>
            <Route exact path="/"><Listing sortBy='hot' /></Route>
            <Route path="/new"><Listing sortBy='new' /></Route>
            <Route path="/rising"><Listing sortBy='rising' /></Route>
            <Route path="/top"><Listing sortBy='top' /></Route>
            <Route path="/active"><Listing sortBy='active' /></Route>
            <Route path="/feeds"><Feeds /></Route>
            <Route path="/create"><Create /></Route>
            <Route path="/feed/:feedId/:feedName/new"><Listing customFeed sortBy='new' /></Route>
            <Route path="/feed/:feedId/:feedName/rising"><Listing customFeed sortBy='rising' /></Route>
            <Route path="/feed/:feedId/:feedName/active"><Listing customFeed sortBy='active' /></Route>
            <Route path="/feed/:feedId/:feedName/top"><Listing customFeed sortBy='top' /></Route>
            <Route path="/feed/:feedId/:feedName"><Listing customFeed sortBy='hot' /></Route>
            <Route path="/c/:community/p/:uuid/:title/c/:commentUuid/"><Post/></Route>
            <Route path="/c/:community/p/:uuid/"><Post /></Route>
            <Route path="/c/:community/submit"><Submit/></Route>
            <Route path="/c/:community/new"><Listing sortBy='new' /></Route>
            <Route path="/c/:community/rising"><Listing sortBy='rising' /></Route>
            <Route path="/c/:community/top"><Listing sortBy='top' /></Route>
            <Route path="/c/:community/active"><Listing sortBy='active' /></Route>
            <Route path="/c/:community/queue"><Queue /></Route>
            <Route path="/c/:community/logs"><Logs /></Route>
            <Route path="/c/:community/ban"><Ban /></Route>
            <Route path="/c/:community/about"><AboutCommunity /></Route>
            <Route path="/c/:community/rules"><RulesCommunity /></Route>
            <Route path="/c/:community/related"><RelatedCommunity /></Route>
            <Route path="/c/:community/wiki"><WikiCommunity /></Route>
            <Route path="/c/:community/config/settings"><CommunitySettings /></Route>
            <Route path="/c/:community/config/basic"><BasicCommunitySettings /></Route>
            <Route path="/c/:community/config/design"><DesignCommunitySettings /></Route>
            <Route path="/c/:community/config/topics"><TopicsCommunitySettings /></Route>
            <Route path="/c/:community/config/moderators"><ModeratorsCommunitySettings /></Route>
            <Route path="/c/:community/config/filter"><FilterCommunitySettings /></Route>
            <Route path="/c/:community/config/stylesheet"><StylesheetCommunitySettings /></Route>
            <Route path="/c/:community/config/rules"><RulesCommunitySettings /></Route>
            <Route path="/c/:community/config/voting"><VotingCommunitySettings /></Route>
            <Route path="/c/:community/config/content"><ContentCommunitySettings /></Route>
            <Route path="/c/:community/config/privacy"><PrivacyCommunitySettings /></Route>
            <Route path="/c/:community"><Listing sortBy='hot' /></Route>
            <Route path="/u/:user"><Profile /></Route>

            <Route path="/settings/notifications"><NotificationsSettings /></Route>
            <Route path="/settings/privacy"><PrivacySettings /></Route>
            <Route path="/settings/password"><PasswordSettings /></Route>
            <Route path="/settings/purge"><PurgeSettings /></Route>
            <Route path="/settings/delete"><DeleteSettings /></Route>
            <Route path="/settings/blocked"><Blocks /></Route>
            <Route path="/settings/blocked-communities"><BlockedCommunities /></Route>
            <Route path="/settings/email"><EmailSettings /></Route>
            <Route path="/settings/appearance"><AppearanceSettings /></Route>
            <Route path="/settings/general"><GeneralSettings /></Route>
            <Route path="/settings/profile"><ProfileSettings /></Route>
            <Route path="/settings/subscription"><SubscriptionSettings /></Route>
            <Route path="/settings"><Settings /></Route>

            <Route path="/p/:uuid/:title/c/:commentUuid/"><Post/></Route>
            <Route path="/p/:uuid/"><Post /></Route>
            <Route path="/communities"><Communities /></Route>
            <Route path="/messages"><Messages /></Route>
            <Route path="/message/:id/"><Message /></Route>
            <Route path="/login"><Login /></Route>
            <Route path="/registration"><Registration /></Route>
            <Route path="/reset"><Reset /></Route>
            <Route path="/change"><Change /></Route>
            <Route path="/submit"><Submit /></Route>
            <Route path="/hidden"><AltPosts title='Hidden Posts' type='hidden' /></Route>
            <Route path="/upvoted"><AltPosts title='Upvoted Posts' type='upvoted' /></Route>
            <Route path="/blocked"><Blocks /></Route>
            <Route path="/blocks"><Blocks /></Route>
            <Route path="/saved/:folder/"><SaveFolder /></Route>
            <Route path="/saved"><Saved /></Route>
            <Route path="/search"><Search title='Search' /></Route>
            <Route path="/send"><Send /></Route>
            <Route path="/browse"><Browse /></Route>
        </Switch>
    );
};

const Themed = ({children}) => {
    const settings = useStoreProfile();
    const theme = useTheme(settings.theme);
    const { colorMode, toggleColorMode } = useColorMode();
    useEffect(() => {
        if (settings.theme !== colorMode) {
            toggleColorMode();
        }
    }, [settings.theme, colorMode])
    return (
        <ThemeProvider theme={theme}>
            {children}
        </ThemeProvider>
    );
};

const FullScreenPopup = () => {
    const [isVisible, setIsVisible] = useState(false);
    const { width } = useWindowDimensions();
    const loggedIn = useLoggedIn();

    useEffect(() => {
        // check localStorage for the closed timestamp and "utm" key
        const closedTimestamp = localStorage.getItem("TFFAGold_Closed");
        const utmPresent = localStorage.getItem("utm");
        const { host } = window.location;

        // only show the popup if the hostname is correct and "utm" is present
        if (host === "patriots.win" && !closedTimestamp && utmPresent) {
            const timer = setTimeout(() => {
                if (!loggedIn) {
                    setIsVisible(true);
                }
            }, 7000);

            return () => clearTimeout(timer);
        }

        return () => {};
    }, [loggedIn]);

    const handleClose = () => {
        setIsVisible(false);
        // store the current timestamp in localStorage
        localStorage.setItem("TFFAGold_Closed", Date.now());
    };

    if (!isVisible) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}
            onClick={handleClose}
        >
            <div
                style={{
                    position: 'relative',
                    maxWidth: '90%',
                    maxHeight: '90%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <a
                    href="https://offers.proudpatriots.com/order-form-fighting-for-america-gold-coin?utm_source=affiliatetraffic&utm_medium=76&oid=165&_ef_transaction_id=&affid=76&oid=165&affid=76"
                    target="_blank"
                    onClick={handleClose}
                >
                    <img src="https://assets.efusercontent.com/1149/offers/165/c/813/700x500_TrumpFightingForAmericaGoldCoin_F+S.png"
                         style={{ maxWidth: '100%', maxHeight: '100%' }} />
                </a>
                <button
                    type="button"
                    onClick={handleClose}
                    style={{
                        position: 'absolute',
                        top: width > 768 ? '-20px' : '-16px',
                        right: width > 768 ? '-20px' : '-16px',
                        width: width > 768 ? '40px' : '32px',
                        height: width > 768 ? '40px' : '32px',
                        borderRadius: '50%',
                        backgroundColor: '#ff4500',
                        color: 'white',
                        border: '2px solid white',
                        fontSize: width > 768 ? '20px' : '16px',
                        fontWeight: 'bold',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                        zIndex: 1001,
                        padding: 0,
                        touchAction: 'manipulation'
                    }}
                    aria-label="Close popup"
                >
                    ✕
                </button>
            </div>
        </div>
    );
};

const App = ({sagaMiddleware, store, actions, readyCh}) => {
    const [modalElement, modal] = useModalStack();
    const [bottomBar, setBottomBar] = useState(0);
    const [fab, setFab] = useState(0);
    const [trendingToggles, setTrendingToggles] = useState(0);
    const appContext = useMemo(() => ({
        store, actions, sagaMiddleware, modal, readyCh, setBottomBar, setFab, setTrendingToggles
    }), [store, actions, sagaMiddleware, modal, readyCh, setBottomBar, setFab, setTrendingToggles]);

    const {pathname} = location;
    useEffect(() => {
        // check if Umami is already loaded
        if (window.umami) {
            // window.umami.trackView(pathname); // track pageview if Umami is loaded
        } else {
            // if Umami is not loaded, load the script
            const script = document.createElement('script');
            script.src = 'https://analytics.harbor.rodeo/script.js';
            script.defer = true;
            script.setAttribute('data-website-id', useUmamiTag());
            document.head.appendChild(script);
        }
    }, [pathname]);

    return (
        <AppContext.Provider value={appContext}>
            <Provider store={store}>
                <Themed>
                    <ModalProvider>
                        <AppContainer id='app'>
                            <ErrorBoundary>

                                <MainRouter/>

                                <Bottom>
                                    <StyledToastContainer
                                        position="bottom-center"
                                        autoClose={3000}
                                        hideProgressBar={false}
                                        newestOnTop
                                        closeOnClick
                                    />
                                    {fab ? <FabWrapper community={fab} isTrendingToggles={trendingToggles}/> : <></>}
                                    {bottomBar > 0 && <BottomHeadroom><BottomBar/></BottomHeadroom>}
                                    <TrackingPixels />
                                    <FullScreenPopup />
                                </Bottom>

                                {modalElement}

                            </ErrorBoundary>
                        </AppContainer>
                        <ModalContainer />
                    </ModalProvider>
                </Themed>
            </Provider>
        </AppContext.Provider>
    );
};

export default App;

if (import.meta.hot) {
    import.meta.hot.accept();
}
